import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { CardBrandIcon } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { SavedCardInstrument } from '@/lib/paycom/types';

interface Props {
  cards: SavedCardInstrument[];
  selectedId: string | null;
  onSelect: (card: SavedCardInstrument) => void;
  onAddNew: () => void;
  onDelete: (cardId: string) => void;
}

/**
 * Saved-card list for the "Switch Card" view. Each card is a tall pill with
 * its brand mark, masked PAN, and an inline trash. Tapping a card selects it
 * (the parent returns to the deposit screen with it inline); tapping the
 * trash asks "Are you sure?" with Yes/No right under the row (design
 * 1940-142031). The "Add new card" link routes to the new-card form.
 */
export function CardSection({ cards, selectedId, onSelect, onAddNew, onDelete }: Props) {
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <span className="text-[18px] font-semibold text-ink">Credit Card</span>
      <button
        type="button"
        onClick={onAddNew}
        className="flex items-center gap-1 self-end rounded px-1 text-sm font-medium text-purple underline hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
      >
        <Plus className="size-4" strokeWidth={2.5} /> Add new card
      </button>

      <ul className="flex flex-col gap-2.5">
        {cards.map((card) => {
          const isSelected = card.id === selectedId;
          const isPendingDelete = card.id === pendingDeleteId;
          return (
            <li key={card.id} className="flex flex-col">
              {/* Single pill row per design: the select area fills it, the
                  trash sits inside the pill's right edge. */}
              <div
                className={cn(
                  'flex h-[52px] items-center rounded-[40px] border-2 transition-all duration-150',
                  isSelected || isPendingDelete
                    ? 'border-purple bg-input-soft/60 shadow-card'
                    : 'border-stroke-soft bg-surface hover:border-purple/40',
                )}
              >
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onSelect(card)}
                  className="flex h-full flex-1 items-center gap-3 rounded-l-[40px] pl-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40"
                >
                  <CardBrandIcon brand={card.brand} size={40} />
                  <span className="font-mono text-[18px] font-light tracking-wider text-ink">•••• {card.last4}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDeleteId(isPendingDelete ? null : card.id)}
                  aria-label={`Remove card ending in ${card.last4}`}
                  aria-expanded={isPendingDelete}
                  className="mr-2 flex size-9 shrink-0 items-center justify-center rounded-full text-ink-muted/60 transition-colors duration-150 hover:bg-purple/10 hover:text-purple focus-visible:outline-none focus-visible:bg-purple/10 focus-visible:text-purple focus-visible:ring-2 focus-visible:ring-purple/40"
                >
                  <Trash2 className="size-4" strokeWidth={2} />
                </button>
              </div>
              {isPendingDelete && (
                <DeleteConfirmRow
                  brand={card.brand}
                  last4={card.last4}
                  onCancel={() => setPendingDeleteId(null)}
                  onConfirm={() => {
                    setPendingDeleteId(null);
                    onDelete(card.id);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface DeleteConfirmRowProps {
  brand: string;
  last4: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/** "Are you sure?  [Yes] [No]" line under the card being removed (design 1940-142031). */
function DeleteConfirmRow({ brand, last4, onCancel, onConfirm }: DeleteConfirmRowProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const pillClass =
    'w-[72px] rounded-pill bg-purple py-1.5 text-sm font-semibold text-white shadow-card transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-2';

  return (
    <div
      role="alertdialog"
      aria-label={`Confirm removal of ${brand} card ending in ${last4}`}
      className="flex items-center justify-between gap-3 px-4 pt-2.5"
    >
      <span className="text-[16px] font-semibold text-purple">Are you sure?</span>
      <div className="flex items-center gap-2">
        <button ref={confirmRef} type="button" onClick={onConfirm} className={pillClass}>
          Yes
        </button>
        <button type="button" onClick={onCancel} className={pillClass}>
          No
        </button>
      </div>
    </div>
  );
}
